/**
 * The header, on a phone.
 *
 * The desktop bar was being reused at 375px with a handful of `narrow ?`
 * branches, and it did not fit: a logo, a back arrow, a title, an update
 * pill, a 248px search box that only shrank to 42, a create button, a bell
 * and an avatar. Every screen in the app scrolled sideways because of it -
 * between 2 and 54 pixels depending on the screen, which is exactly the kind
 * of fault that reads as "this app is broken on my phone" without anybody
 * being able to say why.
 *
 * So this is its own thing rather than a narrower version of that one. A
 * phone header has room for a title and two or three taps, and the rule is
 * that everything on it is at least 44px so it can be hit with a thumb.
 *
 * What was dropped and where it went:
 *
 *   The search FIELD becomes an icon. A phone keyboard covers half the
 *   screen anyway, so the field bought nothing that the icon does not.
 *   Create Party is on Home and on Parties as a real button, which is where
 *   somebody is when they want it - it did not need to be in the chrome too.
 *   The update pill moves to Settings, because an update is not something
 *   you act on mid-scroll.
 */

import { useState } from 'react';
import { useSession } from '@/stores/session';
import { useHistory } from '@/lib/history';
import { fileUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { NotificationBell } from './NotificationPeek';
import { StatusMenu } from './StatusMenu';

/** What each screen is called, at the top. */
const TITLES: Record<string, string> = {
  home: 'Home',
  parties: 'Parties',
  chat: 'Chat',
  friends: 'Friends',
  games: 'Games',
  settings: 'Settings',
  notifications: 'Notifications',
  badges: 'Badges',
  leaderboard: 'Leaderboard',
  crew: 'Crew',
  crews: 'Crews',
  u: 'Profile',
  staff: 'Staff',
  status: 'Server'
};

/** The five the tab bar owns. On those, back would go nowhere useful. */
const ROOTS = new Set(['home', 'parties', 'chat', 'friends', 'settings']);

export function PhoneBar({ route, onSearch }: { route: string; onSearch: () => void }) {
  const me = useSession((s) => s.me);
  const connection = useSession((s) => s.connection);
  const [statusOpen, setStatusOpen] = useState(false);
  const { canBack, back } = useHistory();

  /*
   * Back only where it means something.
   *
   * It was drawn on every screen including the five the tab bar already
   * owns, so Chat had a back arrow that took you somewhere you had not
   * asked to leave. On a root tab the tab bar IS the navigation.
   */
  const showBack = canBack && !ROOTS.has(route);

  return (
    <header
      data-chrome="nav"
      className="relative z-40 flex h-[54px] flex-none items-center gap-1 border-b border-line-soft bg-surface-0 pr-1.5 pl-1.5"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(54px + env(safe-area-inset-top, 0px))' }}
    >
      {showBack ? (
        <button
          type="button"
          onClick={back}
          aria-label="Back"
          className="grid h-11 w-11 flex-none place-items-center rounded-full text-dim outline-none transition-[background-color,color] duration-[var(--t-hover)] active:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          <Icon name="chevronL" size={22} />
        </button>
      ) : (
        <img
          src="./logo.png"
          alt=""
          width={28}
          height={28}
          className="ml-2 flex-none rounded-[calc(9px*var(--radius-mul,1))] object-contain"
        />
      )}

      <h1 className={cn('m-0 min-w-0 flex-1 truncate text-[17px] font-bold text-ink', showBack ? 'pl-0.5' : 'pl-2.5')}>
        {TITLES[route] ?? 'Pugo'}
      </h1>

      {/* Three taps at most, each a full thumb target. */}
      <button
        type="button"
        onClick={onSearch}
        aria-label="Search"
        className="grid h-11 w-11 flex-none place-items-center rounded-full text-dim outline-none transition-[background-color,color] duration-[var(--t-hover)] active:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <Icon name="search" size={20} />
      </button>

      <NotificationBell />

      <span className="relative flex-none">
        <button
          type="button"
          onClick={() => setStatusOpen((v) => !v)}
          aria-label="You and your status"
          className="relative grid h-11 w-11 place-items-center rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          <img
            src={me?.avatarUrl ? fileUrl(me.avatarUrl) : './logo.png'}
            alt=""
            className="h-[32px] w-[32px] rounded-full object-cover"
          />
          <span
            className={cn(
              'absolute right-1 bottom-1 h-[9px] w-[9px] rounded-full ring-2 ring-[var(--bg-0)]',
              connection === 'open' ? 'bg-good' : connection === 'closed' ? 'bg-bad' : 'bg-warn'
            )}
          />
        </button>
        {statusOpen ? <StatusMenu anchorRight onClose={() => setStatusOpen(false)} /> : null}
      </span>
    </header>
  );
}
